import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type DraftType = 'disclosure' | 'resume' | 'interview';

interface UseDraftStorageOptions {
  draftType: DraftType;
  autoSaveInterval?: number; // milliseconds
  expiresInDays?: number;
}

export function useDraftStorage<T>(options: UseDraftStorageOptions) {
  const { draftType, autoSaveInterval = 30000, expiresInDays = 7 } = options;
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Load draft on mount
  useEffect(() => {
    loadDraft();
  }, [draftType]);

  // Auto-save effect
  useEffect(() => {
    if (!data || !autoSaveInterval) return;

    const timer = setInterval(() => {
      saveDraft(data);
    }, autoSaveInterval);

    return () => clearInterval(timer);
  }, [data, autoSaveInterval]);

  const loadDraft = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: draft, error } = await supabase
        .from('user_drafts')
        .select('draft_data, expires_at')
        .eq('user_id', user.id)
        .eq('draft_type', draftType)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      // Check if draft is expired
      if (draft && new Date(draft.expires_at) > new Date()) {
        setData(draft.draft_data as T);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveDraft = async (draftData: T) => {
    try {
      setIsSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expiresInDays);

      // Check if draft exists
      const { data: existing } = await supabase
        .from('user_drafts')
        .select('id')
        .eq('user_id', user.id)
        .eq('draft_type', draftType)
        .maybeSingle();

      let error;
      if (existing) {
        // Update existing draft
        ({ error } = await supabase
          .from('user_drafts')
          .update({
            draft_data: draftData as any,
            expires_at: expiresAt.toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id));
      } else {
        // Insert new draft
        ({ error } = await supabase
          .from('user_drafts')
          .insert({
            user_id: user.id,
            draft_type: draftType,
            draft_data: draftData as any,
            expires_at: expiresAt.toISOString(),
          }));
      }

      if (error) throw error;

      console.log(`Draft auto-saved (${draftType})`);
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const clearDraft = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('user_drafts')
        .delete()
        .eq('user_id', user.id)
        .eq('draft_type', draftType);

      if (error) throw error;

      setData(null);
      toast({
        title: 'Draft cleared',
        description: 'Your saved draft has been removed.',
      });
    } catch (error) {
      console.error('Error clearing draft:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear draft.',
        variant: 'destructive',
      });
    }
  };

  return {
    data,
    setData,
    isLoading,
    isSaving,
    saveDraft,
    clearDraft,
    loadDraft,
  };
}
